import { apiClient, unwrap } from './client';
import type { ApiEnvelope } from './types';

/**
 * OL-COMM-01 commercial spine — READ-ONLY surfacing (OPSFL-IAMUI-1). The RATIFIED contract is
 * contracts/openapi/v1/commercial-api.v1.yaml; wire shapes mirror CommercialDtos. This module
 * deliberately exposes ONLY the GET surface (`commercial.read`: hub_admin, auditor): commercials
 * are recorded by the implementation team through the admin API — the console never edits pricing.
 * Money is MICROS of the contract currency (bigint) everywhere; the SPA renders server-computed
 * amounts and never does pricing math.
 */

const enc = (s: string) => encodeURIComponent(s);

/** A graduated tier band; `upTo === null` is the open top band. */
export interface TierBand {
  upTo: number | null;
  rateMicros: number;
}

export interface RateView {
  unitKey: string;
  rateMicros: number;
  ratingMethod: string;
  tiers: TierBand[] | null;
}

/** One itemised charge (implementation / escalation / other). */
export interface FeeItem {
  key: string;
  amountMicros: number;
  recurrence: string;
}

export interface ContractView {
  contractId: string;
  status: string;
  currency: string | null;
  platformFeeMicros: number | null;
  fees: FeeItem[] | null;
  invoicingAccountRef: string | null;
  licenseRequired: boolean;
  validFrom: string | null;
  validTo: string | null;
  rates: RateView[] | null;
  updatedBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** One rated line: kind is METERED | PLATFORM_FEE | FEE. */
export interface RatingLineItem {
  key: string;
  kind: string;
  count: number;
  rateMicros: number;
  amountMicros: number;
}

export interface RatingView {
  contractId: string;
  periodStart: string | null;
  periodEnd: string | null;
  status: string;
  /** Metered unit → quantity consumed in the period (the meter passthrough). */
  metered: Record<string, number> | null;
  /** Metered unit → which meter source produced it (sources-honesty provenance). */
  meterSources: Record<string, string> | null;
  lineItems: RatingLineItem[] | null;
  totalMicros: number;
  ratedAt: string | null;
}

export interface LicenseView {
  licenseId: string;
  contractId: string;
  licenseType: string;
  status: string;
  entitlements: Record<string, unknown> | null;
  validFrom: string | null;
  validTo: string | null;
  signatureAlgorithm: string | null;
  signatureKeyId: string | null;
  signature: string | null;
  issuedBy: string | null;
  createdAt: string | null;
}

/** The billing-wiring evidence the CommercialShipGate reads at go-live. */
export interface WiringView {
  contractId: string;
  status: string; // WIRED | INCOMPLETE
  contractActive: boolean;
  meterBound: boolean;
  ratingBound: boolean;
  licenseState: string | null;
  invoicingSeamRef: string | null;
  invoicingHandshakeAt: string | null;
  reasons: string[] | null;
  evaluatedAt: string | null;
}

/** GET /v1/commercial/contracts — the hub's contracts, rate card inlined. Defensive on shape. */
export async function listContracts(): Promise<ContractView[]> {
  const { data } = await apiClient.get<ApiEnvelope<{ hub?: string; contracts?: ContractView[] }>>(
    '/v1/commercial/contracts',
  );
  const body = unwrap(data);
  return Array.isArray(body?.contracts) ? body.contracts : [];
}

/** GET /v1/commercial/contracts/{id}/rating — rating runs (dry-run DRAFTs + SEALED periods). */
export async function listRatingRuns(contractId: string): Promise<RatingView[]> {
  const { data } = await apiClient.get<ApiEnvelope<{ runs?: RatingView[] }>>(
    `/v1/commercial/contracts/${enc(contractId)}/rating`,
  );
  const body = unwrap(data);
  return Array.isArray(body?.runs) ? body.runs : [];
}

/** GET /v1/commercial/contracts/{id}/licenses. */
export async function listLicenses(contractId: string): Promise<LicenseView[]> {
  const { data } = await apiClient.get<ApiEnvelope<{ licenses?: LicenseView[] }>>(
    `/v1/commercial/contracts/${enc(contractId)}/licenses`,
  );
  const body = unwrap(data);
  return Array.isArray(body?.licenses) ? body.licenses : [];
}

/** GET /v1/commercial/contracts/{id}/wiring — evaluated FRESH server-side (never cached client-side). */
export async function getWiring(contractId: string): Promise<WiringView> {
  const { data } = await apiClient.get<ApiEnvelope<WiringView>>(
    `/v1/commercial/contracts/${enc(contractId)}/wiring`,
  );
  return unwrap(data);
}

/**
 * Render server-computed micros as a currency amount. DISPLAY conversion only (÷ 1e6) — the number
 * itself always comes from the server; the SPA never rates, sums, or prorates.
 */
export function formatMicros(micros: number | null | undefined, currency?: string | null): string {
  if (micros == null) return '—';
  const amount = micros / 1_000_000;
  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
  return currency ? `${formatted} ${currency}` : formatted;
}
